import type { LngLat, PlaceMatch } from '@chukua/common';
import { AutoComplete, Button, Input } from 'antd';
import clsx from 'clsx';
import { MapPinOff, Search } from 'lucide-react';
import type { RefObject } from 'react';
import { useLocationPicker } from '../../../shared/features/maps/hooks/use-location-picker';
import { SEARCH_INPUT_PROPS } from '../../../shared/policies/search-input-props';
import { Skeleton } from '../../../shared/components/Skeleton';
import { SkeletonRegion } from '../../../shared/components/SkeletonRegion';
import { placeSearchEmptyMessage, placeSearchOptions, placeSearchPick } from './place-search-options';
import { usePlaceSearch } from './use-place-search';
import { useZonesMapConfig } from './use-zones-map-config';

interface LocationPickerFieldProps {
  value: LngLat | null;
  onChange: (location: LngLat | null) => void;
  /** A place picked by name, in addition to `onChange`: the caller fills a sibling field from it (an address, say). */
  onPlacePicked?: (place: PlaceMatch) => void;
  searchAriaLabel: string;
  searchPlaceholder: string;
  /** Shown while nothing is pinned yet; says what this location is for and how to set it. */
  emptyCaption: string;
  /** A compact company-settings card gets a small preview; a record with room to spare gets a bigger one. */
  mapHeightClassName?: string;
}

function describe(location: LngLat): string {
  return `${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}`;
}

/**
 * One point, found by name then fine-tuned by clicking the map or dragging the pin: the shape the company's own
 * location (gitaumoses4/chukua#47) needs. Zones owns it because it already owns the place search and the map
 * config this draws from. A garage no longer uses it: it picks a zone (gitaumoses4/chukua#421).
 */
export function LocationPickerField({
  value,
  onChange,
  onPlacePicked,
  searchAriaLabel,
  searchPlaceholder,
  emptyCaption,
  mapHeightClassName = 'h-56',
}: LocationPickerFieldProps) {
  const mapConfig = useZonesMapConfig();
  const tileKey = mapConfig?.tileKey ?? null;
  const picker = useLocationPicker({ tileKey, value, onChange });
  const search = usePlaceSearch(null, { includeZones: false });

  function pickPlace(place: PlaceMatch) {
    const location = { lng: place.center[0], lat: place.center[1] };
    onChange(location);
    picker.showLocation(location);
    onPlacePicked?.(place);
  }

  function select(option: string) {
    const pick = placeSearchPick(option, search.state);
    search.setText('');
    if (pick?.type === 'place') pickPlace(pick.place);
  }

  return (
    <div className="flex flex-col gap-2">
      <AutoComplete
        className="w-full"
        value={search.text}
        options={placeSearchOptions(search.state)}
        onSearch={search.setText}
        onSelect={select}
        notFoundContent={placeSearchEmptyMessage(search.state)}
      >
        <Input {...SEARCH_INPUT_PROPS} aria-label={searchAriaLabel} prefix={<Search className="size-4 text-muted" />} placeholder={searchPlaceholder} />
      </AutoComplete>
      <LocationMap tileKey={tileKey} isLoading={mapConfig === undefined} containerRef={picker.containerRef} heightClassName={mapHeightClassName} />
      <div className="flex items-center justify-between gap-3 text-xs text-muted">
        <span>{value ? `Pinned at ${describe(value)}. Click the map or drag the pin to move it.` : emptyCaption}</span>
        {value && (
          <Button size="small" type="link" className="px-0" onClick={() => onChange(null)}>
            Clear location
          </Button>
        )}
      </div>
    </div>
  );
}

interface LocationMapProps {
  tileKey: string | null;
  isLoading: boolean;
  containerRef: RefObject<HTMLDivElement | null>;
  heightClassName: string;
}

function LocationMap({ tileKey, isLoading, containerRef, heightClassName }: LocationMapProps) {
  if (isLoading) {
    return (
      <SkeletonRegion label="Loading map">
        <Skeleton className={clsx(heightClassName, 'rounded-lg')} />
      </SkeletonRegion>
    );
  }
  if (!tileKey) {
    return (
      <p className="flex items-center gap-2 rounded-lg border border-dashed border-line bg-page px-3 py-4 text-sm text-muted">
        <MapPinOff className="size-4 shrink-0" />
        No map on this deployment (no MapTiler key). Find the location by name instead.
      </p>
    );
  }
  return <div ref={containerRef} className={clsx(heightClassName, 'w-full rounded-lg border border-line')} />;
}
